import { Box } from '@mui/material';
import { type ReactNode } from 'react';

import {
  type ArrowEdgeRoute,
  DiagramEdgeRoute,
  GraphVizIconShape,
} from '@/components/diagrams/diagramStyle';
import { CircleIcon } from '@/components/icons/CircleIcon';
import { Diamond02Icon } from '@/components/icons/Diamond02Icon';
import { RectangleIcon } from '@/components/icons/RectangleIcon';
import { SquareIcon } from '@/components/icons/SquareIcon';
import { TriangleIcon } from '@/components/icons/TriangleIcon';

/** The size of a picker glyph, in pixels. */
export const GLYPH_SIZE = 16;

/** SVG paths of each route from (2, 13) to (14, 3) in a 16px box. */
const ROUTE_PATHS: Record<ArrowEdgeRoute, string> = {
  [DiagramEdgeRoute.Elbow]: 'M2 13 H8 V3 H14',
  [DiagramEdgeRoute.Straight]: 'M2 13 L14 3',
  [DiagramEdgeRoute.Curved]: 'M2 13 C8 13 8 3 14 3',
};

export interface DiagramLineGlyphProps {
  route?: ArrowEdgeRoute;
  /** Stroke width in pixels of the 16px glyph. */
  weight?: number;
  color?: string;
}

/**
 * A 16px drawing of a line with a route and weight.
 * The line pickers use it as their option icons.
 */
export function DiagramLineGlyph({
  route = DiagramEdgeRoute.Straight,
  weight = 1.5,
  color = 'currentColor',
}: DiagramLineGlyphProps) {
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${GLYPH_SIZE} ${GLYPH_SIZE}`}
      width={GLYPH_SIZE}
      height={GLYPH_SIZE}
      aria-hidden
      sx={{ display: 'block', flexShrink: 0 }}
    >
      <path
        d={ROUTE_PATHS[route]}
        fill="none"
        stroke={color}
        strokeWidth={weight}
      />
    </Box>
  );
}

/** A 16px color sample. `color` is a CSS color or gradient. */
export function DiagramColorSwatch({ color }: { color: string }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: GLYPH_SIZE,
        height: GLYPH_SIZE,
        flexShrink: 0,
        borderRadius: '50%',
        background: color,
      }}
    />
  );
}

/** SVG outlines of each shape in a 16px box. A rectangle is wide. */
const SHAPE_GLYPHS: Record<GraphVizIconShape, ReactNode> = {
  [GraphVizIconShape.Circle]: <CircleIcon size={GLYPH_SIZE} />,
  [GraphVizIconShape.Triangle]: <TriangleIcon size={GLYPH_SIZE} />,
  [GraphVizIconShape.Diamond]: <Diamond02Icon size={GLYPH_SIZE} />,
  [GraphVizIconShape.Square]: <SquareIcon size={GLYPH_SIZE} />,
  [GraphVizIconShape.Rounded]: <RectangleIcon size={GLYPH_SIZE} />,
};

/**
 * A 16px outline of a shape. The shape pickers use it as their option
 * icons. The triangle points down, as the shape node draws it.
 */
export function DiagramShapeGlyph({
  shape,
  color = 'currentColor',
}: {
  shape: GraphVizIconShape;
  color?: string;
}) {
  return (
    <Box
      component="span"
      width={GLYPH_SIZE}
      height={GLYPH_SIZE}
      aria-hidden
      sx={{ display: 'block', flexShrink: 0, color }}
    >
      {SHAPE_GLYPHS[shape]}
    </Box>
  );
}
